import { useEffect, useMemo, useState } from 'react';
import type { User } from '@/types';
import { IS_DEMO_MODE } from '@/config/env';
import {
  getCompanyProjects,
  getProjectCompany,
} from '@/services/ProjectCompanyService';
import {
  deriveCompanyParticipation,
  type CompanyProjectParticipation,
} from '@/types/projectParticipants';
import {
  resolveStakeholderParticipationContext,
  type StakeholderParticipationContext,
} from '@/lib/layout/resolveStakeholderParticipationContext';
import {
  resolveSidebarRouteProjectId,
} from '@/lib/layout/resolvePortalSidebarIdentity';
import { parseCompanyPortalPath } from '@/lib/auth/companyPortal';

export interface UseStakeholderParticipationContextResult
  extends StakeholderParticipationContext {
  loading: boolean;
  error: Error | null;
}

function productionBaseContext(
  user: User | null | undefined,
  pathname: string | null | undefined,
  preferredCompanyId: string | null | undefined,
): StakeholderParticipationContext {
  if (IS_DEMO_MODE) {
    return resolveStakeholderParticipationContext(
      user,
      pathname,
      preferredCompanyId,
    );
  }

  // Never consult demoStore outside demo mode. Resolve only route/authority
  // scope here; canonical project-company participation is loaded below.
  const representedCompanyId = preferredCompanyId?.trim()
    || parseCompanyPortalPath(pathname ?? '').companyId
    || null;
  const routeProjectId = resolveSidebarRouteProjectId(pathname);

  return {
    // Connected chrome never chooses the first project from cached identity
    // claims. Only an explicit route segment binds project context.
    projectId: routeProjectId,
    projectName: null,
    representedCompanyId,
    companyParticipation: null,
  };
}

/**
 * Participation-aware portal context backed by canonical `project_companies`
 * data in linked/staging/production environments and by the showcase store in
 * demo mode. Query failures fail closed to generic Company Portal language.
 */
export function useStakeholderParticipationContext(
  user: User | null | undefined,
  pathname: string | null | undefined,
  preferredCompanyId: string | null | undefined = null,
): UseStakeholderParticipationContextResult {
  const base = useMemo(
    () => productionBaseContext(user, pathname, preferredCompanyId),
    [user, pathname, preferredCompanyId],
  );
  const [context, setContext] = useState<StakeholderParticipationContext>(base);
  const [loading, setLoading] = useState(!IS_DEMO_MODE && Boolean(base.representedCompanyId));
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let cancelled = false;
    setContext(base);
    setError(null);

    if (IS_DEMO_MODE || !base.representedCompanyId) {
      setLoading(false);
      return () => {
        cancelled = true;
      };
    }

    setLoading(true);

    const load = async () => {
      try {
        let projectId = base.projectId;
        let companyParticipation: CompanyProjectParticipation | null = null;

        if (projectId) {
          const projectCompany = await getProjectCompany(
            projectId,
            base.representedCompanyId as string,
          );
          companyParticipation = deriveCompanyParticipation(
            projectCompany?.relationship_type,
          );
        } else {
          // With no active project context, use a portfolio-level label only
          // when every active relationship resolves to the same participation.
          // Mixed Investee/Partner/etc. portfolios keep the generic fallback.
          const projects = await getCompanyProjects(base.representedCompanyId as string);
          if (projects.length === 1) {
            projectId = projects[0].project_id;
          }
          const participations = projects.map((project) =>
            deriveCompanyParticipation(project.relationship_type),
          );
          const uniqueParticipations = new Set(participations);
          if (
            uniqueParticipations.size === 1
            && participations[0] !== null
          ) {
            companyParticipation = participations[0];
          }
        }

        if (cancelled) return;

        setContext({
          ...base,
          projectId,
          companyParticipation,
        });
      } catch (cause) {
        if (cancelled) return;
        setError(cause instanceof Error ? cause : new Error('Unable to resolve portal participation'));
        setContext(base);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [base]);

  return {
    ...context,
    loading,
    error,
  };
}

export default useStakeholderParticipationContext;
